import {
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import CustomButton from "./CustomButton";
import { Notifications as NotificationsIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import api from "../lib/apiClient";
import type { Notification } from "../types";

export default function NotificationBadge() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const result = await api.getNotificationCount(null);
        setCount(result.data.count);
      } catch {
        // Silently ignore — cookie may not be set yet
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    try {
      const result = await api.listNotifications(null);
      setNotifications(result.data);
    } catch {
      // Silently ignore
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead(null);
      setCount(0);
      setNotifications([]);
      handleClose();
    } catch {
      // Silently ignore
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      await api.markNotificationRead(null, notification.id);
      setCount((prev) => Math.max(0, prev - 1));
      navigate(`/tickets`);
      handleClose();
    } catch {
      // Silently ignore
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={open ? "" : t("nav.notifications")}>
        <span>
          <CustomButton
          variant="icon"
          onClick={handleClick}
          aria-label={t("nav.notifications")}
          icon={
            <Badge badgeContent={count} color="error">
              <NotificationsIcon fontSize="small" />
            </Badge>
          }
          iconPosition="only"
          customSize={{ width: 36, height: 36 }}
          iconColor={theme.palette.text.secondary}
          />
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 350, maxHeight: 400 },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t('notificationBadge.title')}</Typography>
          {count > 0 && (
            <CustomButton
              variant="secondary"
              onClick={handleMarkAllRead}
            >
              {t('notificationBadge.markAllRead')}
            </CustomButton>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('notificationBadge.empty')}
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notif) => (
            <MenuItem
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              sx={{ whiteSpace: "normal", py: 1.5 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {notif.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(notif.createdAt).toLocaleString("pt-BR")}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
